<template lang="pug">
section
    //- POS Template toggle (only visible if CMS has shop+POS enabled and not yet sent)
    el-form-item(v-if="hasPosExtension && !hasBeenSent")
        el-switch(
            v-model="isPosTemplate"
            :active-text="$t('POS Template')"
        )
    //- POS Restriction (only visible for POS templates that haven't been sent)
    template(v-if="isPosTemplate && !hasBeenSent")
        .form-section
            el-form-item(:label="$t('POS Access')")
                el-select(
                    v-model="posRestriction"
                    style="width: 100%"
                )
                    el-option(key="all" :label="$t('Accessible by all POSs')" value="all")
                    el-option(key="selected" :label="$t('Restricted to selected POSs')" value="selected")

            el-form-item(v-if="posRestriction === 'selected'" :label="$t('Select POSs')")
                el-select(
                    v-model="selectedPointsOfSale"
                    multiple
                    filterable
                    style="width: 100%"
                    value-key="id"
                    :placeholder="$t('Select POSs...')"
                )
                    el-option(
                        v-for="pos in availablePointsOfSale"
                        :key="pos.id"
                        :label="pos.name"
                        :value="pos"
                    )

    .alert(v-if="isPosTemplate && !hasBeenSent") {{ $t('Recipients will be selected at send time from POS.') }}

    template(v-if="!isPosTemplate")
        .flex-row
            el-form-item.grow(:label="$t('Subscriber List')" prop="subscriptionTopics")
                el-select.is-full-width(
                    placeholder=""
                    v-model="subscriptionTopics"
                    multiple
                    clearable
                    :filterable="options && options.subscriptionTopic.items.length > 10"
                    valueKey="id"
                    :disabled="hasBeenSent"
                )
                    el-option(
                        v-for="subscriptionTopic in options.subscriptionTopic.items"
                        :key="subscriptionTopic.id"
                        :label="subscriptionTopic.name"
                        :value="subscriptionTopic"
                    )
                        .subscription-topic-option
                            span {{ subscriptionTopic.name }}
                            .small.grey {{ $t('Number of subscribers') }}: {{ subscriptionTopic.numberOfSubscribers }}

            el-form-item(:label="$t('Status')")
                el-input(type="text" disabled :value="$t(status)")

        //- Planned Newsletter Info
        .planned-info(v-if="isPlanned")
            .planned-info-header
                i.el-icon-fa-clock.far
                span {{ $t('Planned for') }}: {{ startSendingAtFormatted }}
            .planned-info-actions
                el-button(
                    size="mini"
                    type="warning"
                    plain
                    icon="el-icon-fa-times fas"
                    :loading="cancellingSchedule"
                    :disabled="rescheduling"
                    @click="cancelSchedule"
                ) {{ $t('Cancel Schedule') }}
                el-button(
                    size="mini"
                    type="primary"
                    plain
                    icon="el-icon-fa-calendar-alt far"
                    :disabled="cancellingSchedule"
                    @click="showRescheduleModal"
                ) {{ $t('Change Schedule') }}

        //- Reschedule Modal
        el-dialog.reschedule-dialog(
            :title="$t('Change Schedule')"
            :visible.sync="rescheduleModal.visible"
            width="400px"
            :close-on-press-escape="true"
            :close-on-click-modal="true"
        )
            el-form(label-position="top")
                el-form-item(:label="$t('Scheduled At')")
                    el-date-picker.is-full-width(
                        v-model="rescheduleDate"
                        type="datetime"
                        :placeholder="$t('Select date and time')"
                        :format="$t('yyyy-MM-dd') + '   HH:mm'"
                        :default-time="'12:00:00'"
                        prefix-icon="far el-icon-fa-calendar-alt"
                        :picker-options="{ disabledDate(time) { return time.getTime() < Date.now() - 86400000; } }"
                        :editable="false"
                        :clearable="false"
                    )
            template(slot="footer")
                el-button(@click="rescheduleModal.visible = false") {{ $t('Cancel') }}
                el-button(
                    type="primary"
                    :loading="rescheduling"
                    :disabled="!rescheduleModal.scheduledAt"
                    @click="reschedule"
                ) {{ $t('Save') }}

        .flex-row
            el-form-item.grow(:label="$t('Customer Groups')" prop="customerGroups")
                el-select.is-full-width(
                    placeholder=""
                    v-model="customerGroups"
                    multiple
                    clearable
                    :filterable="options && options.customerGroup && options.customerGroup.items.length > 10"
                    valueKey="id"
                    :disabled="hasBeenSent"
                )
                    el-option(
                        v-for="group in (options && options.customerGroup && options.customerGroup.items) || []"
                        :key="group.id"
                        :label="group.name"
                        :value="group"
                    )

            el-form-item.grow(:label="$t('Member Tags')" prop="memberTags")
                el-select.is-full-width(
                    v-model="memberTags"
                    :placeholder="$t('Search tags...')"
                    multiple
                    filterable
                    remote
                    clearable
                    value-key="id"
                    :remote-method="searchMemberTags"
                    :loading="memberTagSearchLoading"
                    :disabled="hasBeenSent"
                )
                    el-option(
                        v-for="tag in memberTagOptions"
                        :key="tag.id"
                        :label="tag.name"
                        :value="tag"
                    )

    //- Calculated Recipient Count (only shown before sending)
    .recipient-count-info(v-if="!hasBeenSent && hasSubscriptionTopics")
        .recipient-count-label {{ $t('Calculated Recipients') }}:
        .recipient-count-value(v-if="!calculatedRecipientCountLoading")
            strong {{ calculatedRecipientCount != null ? calculatedRecipientCount : '-' }}
            span.recipient-count-hint(v-if="hasActiveFilters")  ({{ $t('filtered') }})
        .recipient-count-value(v-else)
            i.el-icon-loading
            span {{ $t('Calculating...') }}

    //- Delivery Timeline Chart
    DeliveryChart(v-if="hasBeenSent" ref="deliveryChart" :newsletter-id="form.id" :started-sending-at="form.started_sending_at")

    //- Delivery Statistics Panel
    el-card.delivery-stats(v-if="hasBeenSent" shadow="never")
        .card-header(slot="header")
            i.el-icon-fa-chart-bar.far
            span {{ $t('Delivery Statistics') }}

        //- Statistics Grid
        .stats-grid
            //- Sending Info Section
            .stat-section
                .section-title {{ $t('Sending Timeline') }}
                .stat-item
                    .stat-label {{ $t('Started') }}
                    .stat-value {{ startedSendingAtFormatted }}
                .stat-item(v-if="form.finished_sending_at")
                    .stat-label {{ $t('Finished') }}
                    .stat-value {{ finishedSendingAtFormatted }}
                .stat-item(v-if="sendingDuration")
                    .stat-label {{ $t('Duration') }}
                    .stat-value {{ sendingDuration }}

            //- Delivery Stats Section
            .stat-section
                .section-title {{ $t('Delivery') }}
                .stat-item
                    .stat-label {{ $t('Total Recipients') }}
                    .stat-value.primary {{ form.recipients || 0 }}
                .stat-item(v-if="sentEmailsCount != null")
                    .stat-label {{ $t('Sent Emails') }}
                    .stat-value {{ sentEmailsCount }}
                        span.percentage(v-if="form.recipients") ({{ sentPercentage }}%)
                .stat-item(v-if="receivedCount != null")
                    .stat-label {{ $t('Delivered') }}
                    .stat-value.success {{ receivedCount }}
                        span.percentage(v-if="form.recipients") ({{ receivedPercentage }}%)
                .stat-item(v-if="form.skipped != null && form.skipped > 0")
                    .stat-label {{ $t('Skipped') }}
                    .stat-value.warning {{ form.skipped || 0 }}
                        span.percentage(v-if="form.recipients") ({{ skippedRate }}%)
                .stat-item(v-if="form.bounces != null")
                    .stat-label {{ $t('Bounces') }}
                    .stat-value.danger {{ form.bounces || 0 }}
                        span.percentage(v-if="form.recipients") ({{ bounceRate }}%)

            //- Engagement Stats Section
            .stat-section
                .section-title {{ $t('Engagement') }}
                .stat-item(v-if="opensCount != null")
                    .stat-label {{ $t('Opens') }}
                    .stat-value.info {{ opensCount }}
                        span.percentage(v-if="form.recipients") ({{ openRate }}%)
                .stat-item(v-if="clicksCount != null")
                    .stat-label {{ $t('Clicks') }}
                    .stat-value.shop-primary.clickable(@click="openUrlClicksModal")
                        span.click-details-hint
                            span.hint-icon ?
                            span.hint-value {{ clicksCount }}
                        span.percentage(v-if="opensCount") ({{ clickThroughRate }}%)

    //- Sent Emails List
    SentEmailsList(v-if="hasBeenSent" ref="sentEmailsList" :newsletter-id="form.id")

    //- Email Preview Drawer
    SentEmailPreviewDrawer

    //- URL Clicks Modal
    UrlClicksModal
</template>

<i18n>
{
    "en": {
        "sent": "Sent",
        "draft": "Draft",
        "planned": "Planned",
        "filtered": "filtered",
        "pos-template": "POS Template"
    },
    "de": {
        "sent": "Gesendet",
        "draft": "Entwurf",
        "planned": "Geplant",
        "Planned for": "Geplant für",
        "Cancel Schedule": "Zeitplan abbrechen",
        "Change Schedule": "Zeitplan ändern",
        "Schedule cancelled successfully": "Zeitplan erfolgreich abgebrochen",
        "Failed to cancel schedule": "Zeitplan konnte nicht abgebrochen werden",
        "Schedule updated successfully": "Zeitplan erfolgreich aktualisiert",
        "Failed to update schedule": "Zeitplan konnte nicht aktualisiert werden",
        "Select date and time": "Datum und Uhrzeit auswählen",
        "Scheduled At": "Geplant für",
        "Cancel": "Abbrechen",
        "Save": "Speichern",
        "POS Template": "POS-Vorlage",
        "Yes": "Ja",
        "No": "Nein",
        "Recipients will be selected at send time from POS.": "Empfänger werden beim Senden über die Kasse ausgewählt.",
        "Subscriber List": "Abonnenten-Liste",
        "Subscription Topic": "Abonnement-Thema",
        "Number of subscribers": "Anzahl der Abonnenten",
        "Customer Groups": "Kundengruppen",
        "Member Tags": "Mitglieder-Tags",
        "Calculated Recipients": "Berechnete Empfänger",
        "Calculating...": "Berechne...",
        "filtered": "gefiltert",
        "Delivery Statistics": "Versand-Statistiken",
        "Sending Timeline": "Versandzeitplan",
        "Started": "Gestartet",
        "Finished": "Beendet",
        "Duration": "Dauer",
        "Delivery": "Zustellung",
        "Total Recipients": "Anzahl der Empfänger",
        "Sent Emails": "Gesendete E-Mails",
        "Received": "Empfangen",
        "Delivered": "Zugestellt",
        "Bounces": "Bounces",
        "Skipped": "Übersprungen",
        "Engagement": "Interaktion",
        "Opens": "Geöffnet",
        "Clicks": "Geklickt",
        "Search tags...": "Tags suchen...",
        "POS Access": "POS-Zugang",
        "Accessible by all POSs": "Für alle Kassen zugänglich",
        "Restricted to selected POSs": "Auf ausgewählte Kassen beschränkt",
        "Select POSs": "Kassen auswählen",
        "Select POSs...": "Kassen auswählen..."
    }
}
</i18n>

<script>

import _ from 'lodash'
import { getUnixTime, fromUnixTime } from 'date-fns'
import { appFromUnixTime, appGetUnixTime } from '@/utils/timezone'
import { timestamp2date } from '@/filters'
import echo from '@/echo'
import { useSetupStore, useNewsletterNewsletterStore } from '@/stores'
import PointOfSaleApi from '@/extensions/shop/pointOfSale/store/api/pointOfSale'

import DeliveryChart from './DeliveryChart.vue'
import SentEmailsList from './SentEmailsList.vue'
import SentEmailPreviewDrawer from './SentEmailPreviewDrawer.vue'
import UrlClicksModal from './UrlClicksModal.vue'

export default {
    name: 'NewsletterDelivery',
    components: {
        DeliveryChart,
        SentEmailsList,
        SentEmailPreviewDrawer,
        UrlClicksModal
    },
    data() {
        return {
            echoChannel: null,
            debouncedFetchRecipientCount: null,
            debouncedRefetchPanels: null,
            initialRecipientCountFetched: false,
            memberTagSearchLoading: false,
            memberTagSearchResults: [],
            availablePointsOfSale: [],
            cancellingSchedule: false,
            rescheduling: false,
            rescheduleModal: {
                visible: false,
                scheduledAt: null,
            },
        }
    },
    created() {
        // Create debounced version of fetchRecipientCount
        this.debouncedFetchRecipientCount = _.debounce(() => {
            this.fetchRecipientCount()
        }, 300)

        // Create debounced version of panel refresh (for websocket updates)
        this.debouncedRefetchPanels = _.debounce(() => {
            this.refetchChildPanels()
        }, 1000)
    },
    methods: {
        initializeStatusLogListener() {
            if (!echo.client) {
                setTimeout(() => this.initializeStatusLogListener(), 100)
                return
            }

            this.echoChannel = echo.client.private('cms.' + this.activeCms.id)
                // .listen('.status-log:updated', async data => {
                //     if (data.log && ['send-newsletter'].includes(data.log.key)) {
                //         // Refetch newsletter statistics when sending is in progress
                //         await this.refetchStatistics()
                //     }
                // })
                .listen('.newsletter:statistics-updated', async data => {
                    // Listen to real-time tracking events (opens, clicks, bounces, sent)
                    // This keeps the statistics up-to-date in real-time
                    const newsletterId = _.get(this.form, 'id')
                    if (data.id == newsletterId) {
                        console.log('Newsletter statistics updated:', data)
                        if (!this.form.started_sending_at) {
                            this.refetchStatistics()
                        } else {
                            // Update statistics directly without refetching
                            this.updateStatistics(data)
                        }
                    }
                })
        },
        async refetchStatistics() {
            const id = _.get(this.form, 'id')
            if (!id) return

            try {
                await this.fetchNewsletter({
                    id,
                    skipPage: true,
                    skipSubscriptionTopics: true
                })
                // Also refetch child panels when full statistics are refetched
                this.refetchChildPanels()
            } catch (error) {
                console.error('Error refetching newsletter statistics:', error)
            }
        },
        updateStatistics(statistics) {
            // Update the form statistics without refetching
            // Use != null to check for both null and undefined (prevents null values from overwriting real data)
            if (statistics.sentEmailsCount != null) {
                this.newsletterStore.setFormSentEmailsCount(statistics.sentEmailsCount)
            }
            if (statistics.opens != null) {
                this.newsletterStore.setFormOpens(statistics.opens)
            }
            if (statistics.clicks != null) {
                this.newsletterStore.setFormClicks(statistics.clicks)
            }
            if (statistics.bounces != null) {
                this.newsletterStore.setFormBounces(statistics.bounces)
            }
            if (statistics.received != null) {
                this.newsletterStore.setFormReceived(statistics.received)
            }
            if (statistics.skipped != null) {
                this.newsletterStore.setFormSkipped(statistics.skipped)
            }

            // Also trigger debounced refetch of child panels for detailed data
            this.debouncedRefetchPanels()
        },
        refetchChildPanels() {
            // Refetch sent emails list (silent: true to skip loading animation for WebSocket updates)
            if (this.$refs.sentEmailsList && typeof this.$refs.sentEmailsList.refetch === 'function') {
                this.$refs.sentEmailsList.refetch({ silent: true })
            }
            // Refetch timeline chart (silent: true to skip loading animation for WebSocket updates)
            if (this.$refs.deliveryChart && typeof this.$refs.deliveryChart.fetchData === 'function') {
                this.$refs.deliveryChart.fetchData({ silent: true })
            }
        },
        searchMemberTags(query) {
            if (!query || query.length < 2) {
                this.memberTagSearchResults = []
                return
            }
            this.memberTagSearchLoading = true
            this.fetchMemberTagsBySearch(query)
                .then(tags => {
                    this.memberTagSearchResults = tags
                })
                .finally(() => {
                    this.memberTagSearchLoading = false
                })
        },
        openUrlClicksModal() {
            if (this.form && this.form.id) {
                this.newsletterStore.fetchUrlClicks(this.form.id)
            }
        },
        fetchNewsletter(payload) {
            return this.newsletterStore.fetchNewsletter(payload)
        },
        fetchRecipientCount() {
            return this.newsletterStore.fetchRecipientCount()
        },
        fetchMemberTagsBySearch(query) {
            return this.newsletterStore.fetchMemberTagsBySearch(query)
        },
        cancelSchedule() {
            this.cancellingSchedule = true
            this.newsletterStore.cancelPlannedNewsletter(this.form.id)
                .then(() => {
                    this.$notify.success({
                        title: this.$t('Schedule cancelled successfully'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 5000
                    })
                })
                .catch(error => {
                    console.error(error)
                    this.$notify.error({
                        title: this.$t('Failed to cancel schedule'),
                        message: _.get(error, 'message', ''),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    })
                })
                .finally(() => {
                    this.cancellingSchedule = false
                })
        },
        showRescheduleModal() {
            this.rescheduleModal.scheduledAt = _.get(this.form, 'start_sending_at')
            this.rescheduleModal.visible = true
        },
        reschedule() {
            if (!this.rescheduleModal.scheduledAt) return

            this.rescheduling = true
            this.newsletterStore.reschedulePlannedNewsletter(this.form.id, this.rescheduleModal.scheduledAt)
                .then(() => {
                    this.rescheduleModal.visible = false
                    this.$notify.success({
                        title: this.$t('Schedule updated successfully'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 5000
                    })
                })
                .catch(error => {
                    console.error(error)
                    this.$notify.error({
                        title: this.$t('Failed to update schedule'),
                        message: _.get(error, 'message', ''),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    })
                })
                .finally(() => {
                    this.rescheduling = false
                })
        },
        fetchAvailablePointsOfSale() {
            PointOfSaleApi.fetchList()
                .then(items => {
                    this.availablePointsOfSale = items || [];
                })
                .catch(error => {
                    console.error('Error fetching points of sale:', error);
                    this.availablePointsOfSale = [];
                });
        },
    },
    watch: {
        isPosTemplate: {
            handler(value) {
                if (value && this.availablePointsOfSale.length === 0) {
                    this.fetchAvailablePointsOfSale();
                }
            },
            immediate: true
        },
        subscriptionTopics: {
            handler(newValue, oldValue) {
                // Fetch when topics change (including initial load)
                if (!this.hasBeenSent && newValue && newValue.length > 0) {
                    // Initial fetch - only once
                    if (!this.initialRecipientCountFetched) {
                        this.initialRecipientCountFetched = true
                        this.$nextTick(() => {
                            this.fetchRecipientCount()
                        })
                        return
                    }

                    // User changed selection - compare IDs to detect real changes
                    const newIds = (newValue || []).map(t => t.id).sort().join(',')
                    const oldIds = (oldValue || []).map(t => t.id).sort().join(',')
                    if (newIds !== oldIds) {
                        this.debouncedFetchRecipientCount()
                    }
                }
            },
            deep: true,
            immediate: true,
        },
        customerGroups: {
            handler(newValue, oldValue) {
                if (!this.hasBeenSent && this.hasSubscriptionTopics && oldValue !== undefined) {
                    this.debouncedFetchRecipientCount()
                }
            },
            deep: true,
        },
        memberTags: {
            handler(newValue, oldValue) {
                if (!this.hasBeenSent && this.hasSubscriptionTopics && oldValue !== undefined) {
                    this.debouncedFetchRecipientCount()
                }
            },
            deep: true,
        },
    },
    computed: {
        newsletterStore() {
            return useNewsletterNewsletterStore()
        },
        form() {
            return this.newsletterStore.form
        },
        options() {
            return this.newsletterStore.options
        },
        status() {
            if (_.get(this.form, 'started_sending_at')) return 'sent'
            if (_.get(this.form, 'start_sending_at')) return 'planned'
            return 'draft'
        },
        isPlanned() {
            return this.status === 'planned'
        },
        startSendingAtFormatted() {
            const timestamp = _.get(this.form, 'start_sending_at')
            if (!timestamp) return null
            return timestamp2date(timestamp, 'dd.MM.yyyy, HH:mm')
        },
        subscriptionTopics: {
            get() {
                return _.get(this.form, 'subscriptionTopics')
            },
            set(value) {
                this.newsletterStore.setFormSubscriptionTopics(value)
            }
        },
        customerGroups: {
            get() {
                return _.get(this.form, 'customerGroups')
            },
            set(value) {
                this.newsletterStore.setFormCustomerGroups(value)
            }
        },
        memberTags: {
            get() {
                return _.get(this.form, 'memberTags')
            },
            set(value) {
                this.newsletterStore.setFormMemberTags(value)
            }
        },
        memberTagOptions() {
            // Combine search results with already selected tags to keep them visible
            const selected = this.memberTags || []
            const searchResults = this.memberTagSearchResults || []
            const combined = [...selected]
            searchResults.forEach(tag => {
                if (!combined.find(t => t.id === tag.id)) {
                    combined.push(tag)
                }
            })
            return combined
        },
        hasBeenSent() {
            return !!_.get(this.form, 'started_sending_at')
        },
        isSending() {
            return this.hasBeenSent && !_.get(this.form, 'finished_sending_at')
        },
        sendingDuration() {
            const started = _.get(this.form, 'started_sending_at')
            const finished = _.get(this.form, 'finished_sending_at')
            if (!started || !finished) return null

            const durationMs = (finished - started) * 1000
            const minutes = Math.floor(durationMs / 60000)
            const seconds = Math.floor((durationMs % 60000) / 1000)

            if (minutes > 0) {
                return `${minutes}m ${seconds}s`
            }
            return `${seconds}s`
        },
        startedSendingAtFormatted() {
            const timestamp = _.get(this.form, 'started_sending_at')
            if (!timestamp) return null
            return timestamp2date(timestamp, 'dd.MM.yyyy, HH:mm')
        },
        finishedSendingAtFormatted() {
            const timestamp = _.get(this.form, 'finished_sending_at')
            if (!timestamp) return null
            return timestamp2date(timestamp, 'dd.MM.yyyy, HH:mm')
        },
        sentEmailsCount() {
            return _.get(this.form, 'sentEmailsCount', 0)
        },
        receivedCount() {
            return _.get(this.form, 'received', 0)
        },
        opensCount() {
            return _.get(this.form, 'opens', 0)
        },
        clicksCount() {
            return _.get(this.form, 'clicks', 0)
        },
        sentPercentage() {
            const sent = this.sentEmailsCount
            const total = _.get(this.form, 'recipients') || 0
            if (total === 0) return 0
            return Math.round((sent / total) * 100)
        },
        receivedPercentage() {
            const received = this.receivedCount
            const total = _.get(this.form, 'recipients') || 0
            if (total === 0) return 0
            return Math.round((received / total) * 100)
        },
        bounceRate() {
            const bounces = _.get(this.form, 'bounces') || 0
            const total = _.get(this.form, 'recipients') || 0
            if (total === 0) return 0
            return Math.round((bounces / total) * 100)
        },
        skippedRate() {
            const skipped = _.get(this.form, 'skipped') || 0
            const total = _.get(this.form, 'recipients') || 0
            if (total === 0) return 0
            return Math.round((skipped / total) * 100)
        },
        openRate() {
            const opens = this.opensCount
            const total = _.get(this.form, 'recipients') || 0
            if (total === 0) return 0
            return Math.round((opens / total) * 100)
        },
        clickThroughRate() {
            const clicks = this.clicksCount
            const opens = this.opensCount
            if (opens === 0) return 0
            return Math.round((clicks / opens) * 100)
        },
        calculatedRecipientCount() {
            return _.get(this.form, 'calculatedRecipientCount')
        },
        calculatedRecipientCountLoading() {
            return _.get(this.form, 'calculatedRecipientCountLoading', false)
        },
        hasSubscriptionTopics() {
            const topics = _.get(this.form, 'subscriptionTopics') || []
            return topics.length > 0
        },
        hasActiveFilters() {
            const customerGroups = _.get(this.form, 'customerGroups') || []
            const memberTags = _.get(this.form, 'memberTags') || []
            return customerGroups.length > 0 || memberTags.length > 0
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        enabledExtensions() {
            return useSetupStore().enabledExtensions;
        },
        hasPosExtension() {
            return this.enabledExtensions.includes('shop') && this.enabledExtensions.includes('shop:point-of-sale');
        },
        isPosTemplate: {
            get() {
                return _.get(this.form, 'is_pos_template', false);
            },
            set(value) {
                this.newsletterStore.form.is_pos_template = value;
            }
        },
        posRestriction: {
            get() {
                return _.get(this.form, 'pos_restriction', 'all');
            },
            set(value) {
                this.newsletterStore.form.pos_restriction = value;
                if (value === 'all') {
                    this.newsletterStore.form.pointsOfSale = [];
                }
            }
        },
        rescheduleDate: {
            get() {
                var scheduledAt = this.rescheduleModal.scheduledAt
                if (scheduledAt) scheduledAt = appFromUnixTime(scheduledAt)
                return scheduledAt
            },
            set(value) {
                this.rescheduleModal.scheduledAt = value !== null ? appGetUnixTime(value) : null
            }
        },
        selectedPointsOfSale: {
            get() {
                return _.get(this.form, 'pointsOfSale') || [];
            },
            set(value) {
                this.newsletterStore.form.pointsOfSale = value;
            }
        },
    },
    mounted() {
        this.initializeStatusLogListener()
    },
    beforeDestroy() {
        if (this.echoChannel) {
            this.echoChannel.stopListening('.status-log:updated')
            this.echoChannel.stopListening('.newsletter:statistics-updated')
        }
    },
}
</script>

<style lang="sass" scoped>
.subscription-topic-option
    padding-top: 4px
    padding-bottom: 4px
    line-height: 1.3
    .small
        font-size: 0.8em
    .grey
        color: #888

.recipient-count-info
    display: flex
    align-items: center
    gap: 8px
    padding: 12px 16px
    margin-top: 8px
    background: #f0f9eb
    border: 1px solid #e1f3d8
    border-radius: 4px
    font-size: 14px

    .recipient-count-label
        color: #606266

    .recipient-count-value
        display: flex
        align-items: center
        gap: 4px

        strong
            color: #67c23a
            font-size: 16px

        .recipient-count-hint
            color: #909399
            font-size: 12px
            font-style: italic

        i.el-icon-loading
            color: #409eff

.delivery-stats
    margin-top: 20px
    background: #f5f7fa
    border: 1px solid #e4e7ed

    ::v-deep .el-card__header
        padding: 16px 20px !important
        background: #fff
        border-bottom: 1px solid #e4e7ed

    ::v-deep .el-card__body
        padding: 24px 20px !important

    .card-header
        display: flex
        align-items: center
        gap: 8px
        font-weight: 600
        font-size: 14px
        color: #303133

        i
            color: #409eff
            font-size: 14px

    .stats-grid
        display: grid
        grid-template-columns: repeat(3, 1fr)
        gap: 24px

        @media (max-width: 1200px)
            grid-template-columns: 1fr
            gap: 20px

        .stat-section
            min-width: 0

            .section-title
                font-weight: 600
                font-size: 14px
                color: #909399
                text-transform: uppercase
                letter-spacing: 0.8px
                margin-bottom: 12px
                padding-bottom: 6px
                border-bottom: 2px solid #dcdfe6

            .stat-item
                display: flex
                justify-content: space-between
                align-items: center
                padding: 8px 0
                line-height: 1.4

                &:last-child
                    padding-bottom: 0

                .stat-label
                    font-size: 14px
                    color: #606266
                    font-weight: 400

                .stat-value
                    font-weight: 600
                    font-size: 14px
                    color: #303133
                    display: flex
                    align-items: center
                    gap: 4px

                    &.primary
                        color: #409eff

                    &.success
                        color: #67c23a

                    &.danger
                        color: #f56c6c

                    &.info
                        color: #409eff

                    &.warning
                        color: #e6a23c

                    &.shop-primary
                        color: #6E26FF

                    .percentage
                        font-size: 14px
                        font-weight: 500
                        color: #909399
                        margin-left: 2px

                    &.clickable
                        cursor: pointer
                        transition: all 0.2s ease

                        &:hover
                            opacity: 0.8

                        .click-details-hint
                            text-decoration: underline dotted #303133
                            text-underline-offset: 3px
                            transition: opacity 0.2s ease

                            &:hover
                                opacity: 0.7

                            .hint-icon
                                color: #909399
                                margin-right: 4px

                            .hint-value
                                color: #6E26FF

.planned-info
    display: flex
    align-items: center
    justify-content: space-between
    gap: 12px
    padding: 12px 16px
    margin-bottom: 16px
    background: #fdf6ec
    border: 1px solid #faecd8
    border-radius: 4px
    font-size: 14px

    .planned-info-header
        display: flex
        align-items: center
        gap: 8px
        color: #e6a23c
        font-weight: 600

    .planned-info-actions
        display: flex
        gap: 8px

.alert
    margin-bottom: 0

.el-form-item.is-hidden
    display: none
</style>
